import { useBackendApi } from './backendApi';
import { useEventsPublisher, useEventsSubscriber } from 'event-bus';
import { useCacheService } from './cacheService';
import { ShortMarket } from './marketsAdminService';
import { ProjectType } from './Types';
import { SetupAreaVersionStatus } from './setupAreas';

export enum TeamSetupAreaStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export interface TeamSetupArea {
  id: number;
  version: string;
  name: string;
  alias: string;
  timezone?: string;
  status: TeamSetupAreaStatus;
  isDefault: boolean;
  isCustom: boolean;
  market: ShortMarket | null;
  hasSets: boolean;
  versions: {
    version: string;
    status: SetupAreaVersionStatus;
  }[]
  projectType?: ProjectType;
}

export interface AddSetupAreaRequest {
  setupAreaId: number;
  setupAreaVersion: string;
}

export interface TeamSetupAreaLimits {
  used: number,
  total: number,
  remaining: number
}

export const TEAM_SETUP_AREAS_UPDATED = 'TEAM_SETUP_AREAS_UPDATED';

export type TeamSetupAreasFilter = {
  state?: string,
  nameContains?: string,
  onlyActive?: boolean,
}

export function useTeamSetupAreasService() {
  const nekstApi = useBackendApi();

  const eventsPublisher = useEventsPublisher();

  const cacheService = useCacheService('teamSetupAreas');

  useEventsSubscriber(
    'teamSetupAreasService',
    {
      [TEAM_SETUP_AREAS_UPDATED]: () => {
        cacheService.clear('all');
      }
    }
  );

  const getTeamSetupAreas = async (
    filter?: TeamSetupAreasFilter,
  ): Promise<TeamSetupArea[]> => {
    let result = await cacheService.get<TeamSetupArea[]>('all', async () => {
      return await nekstApi.get('/teamsetupareas') as TeamSetupArea[];
    });

    result = result
      .filter((item) => {
        if (filter?.state && item.market?.state !== filter.state) {
          return false;
        }
        if (filter?.nameContains && !item.name.toLowerCase()
          .includes(filter.nameContains.toLowerCase())) {
          return false;
        }

        const onlyActive = filter?.onlyActive !== undefined ? filter.onlyActive : true;

        if (onlyActive && item.status === TeamSetupAreaStatus.ARCHIVED) {
          return false;
        }

        return true;
      });


    return result;
  };

  const addSetupArea = async (request: AddSetupAreaRequest): Promise<TeamSetupArea> => {
    const result = await nekstApi.post('/teamsetupareas', request) as TeamSetupArea;

    eventsPublisher.publish(TEAM_SETUP_AREAS_UPDATED, {});
    return result;
  };

  const archiveSetupArea = async (setupAreaId: number): Promise<void> => {
    await nekstApi.delete(`/teamsetupareas/${setupAreaId}`);
    eventsPublisher.publish(TEAM_SETUP_AREAS_UPDATED, {});
  };

  const getTeamSetupArea = async (setupAreaId: number | string): Promise<TeamSetupArea> => {
    return await nekstApi.get(`/teamsetupareas/${setupAreaId}`) as TeamSetupArea;
  };

  const setDefaultSetupArea = async (setupAreaId: number): Promise<void> => {
    await nekstApi.post(`/teamsetupareas/${setupAreaId}/setdefault`);
    eventsPublisher.publish(TEAM_SETUP_AREAS_UPDATED, {});
  };

  const getLimits = async (): Promise<TeamSetupAreaLimits> => {
    return await nekstApi.get('/teamsetupareas/limits') as TeamSetupAreaLimits;
  }

  return {
    getTeamSetupAreas,
    getTeamSetupArea,
    addSetupArea,
    archiveSetupArea,
    setDefaultSetupArea,
    getLimits,
  };
}
